/**
 * Small display primitives specific to the Apprise section (notice banners, badges).
 * The text / select / radio field primitives live in FormFields.
 */
import type { ReactNode } from 'react'
import { AlertTriangle, Info } from 'lucide-react'
import styles from '@/pages/NewDeploymentPage.module.css'

type Tone = 'info' | 'warning' | 'error'

const TONE_STYLE: Record<Tone, { borderColor: string; background: string }> = {
  info: { borderColor: 'rgba(84, 179, 190, 0.3)', background: 'rgba(84, 179, 190, 0.08)' },
  warning: { borderColor: 'rgba(245, 166, 35, 0.5)', background: 'rgba(245, 166, 35, 0.1)' },
  error: { borderColor: 'rgba(224, 82, 82, 0.5)', background: 'rgba(224, 82, 82, 0.08)' },
}

/** Full-width banner used for notices and warnings inside a form grid. */
export function Banner({ tone = 'info', children, role }: { tone?: Tone; children: ReactNode; role?: string }) {
  const Icon = tone === 'info' ? Info : AlertTriangle
  return (
    <div className={styles.lbInfoBanner} role={role} style={{ gridColumn: '1/-1', marginTop: 0, ...TONE_STYLE[tone] }}>
      <Icon size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
      {children}
    </div>
  )
}

export function Badge({ children, tone = 'warning' }: { children: ReactNode; tone?: 'warning' | 'muted' }) {
  return (
    <span
      style={{
        display: 'inline-block', marginLeft: 8, padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
        background: tone === 'warning' ? 'rgba(245, 166, 35, 0.15)' : 'rgba(107, 130, 153, 0.15)',
        color: tone === 'warning' ? 'var(--aptean-warning)' : 'var(--aptean-text-muted)',
      }}
    >
      {children}
    </span>
  )
}
